import clsx from "clsx";
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { SegmentDefinition } from "@adlywise/shared";
import BuildConditions from "@/components/segments/conditions/BuildConditions";
import {
  buildAnalyticsCategories,
  type AnalyticsCategory,
  type AnalyticsFieldDescriptor,
} from "@/features/analytics/filterCategories";
import { DRAWER_TRANSITION_MS } from "./filters/constants";
import type { AnalyticsConfig } from "./ConfigurePanel";

// Filter drawer for the analytics aggregation report (plan Phase C step 11).
// Renders the SAME shared BuildConditions component the segment wizard uses, in
// Include-only + AND-locked mode, driven by the analytics category set. The
// categories are restricted to the properties the user selected in Configure
// (groupBy) PLUS the whole Campaign category on responses. The value emitted is
// a SegmentDefinition { all: [...] }.
//
// This is intentionally separate from AnalyticsFiltersDrawer (the legacy flat
// filter drawer still used by the Devices page) — the two consume different
// value shapes.

type AnalyticsAggregateFiltersDrawerProps = {
  isOpen: boolean;
  config: AnalyticsConfig;
  value: SegmentDefinition;
  onChange: (nextValue: SegmentDefinition) => void;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
};

const CloseIcon = (): JSX.Element => {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 5L15 15M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
};

// Restrict the analytics categories to the fields the report groups by, so the
// filter only surfaces the configured properties (plan Phase C). The Campaign
// category (response-side) is kept whole on responses regardless of groupBy so
// campaign/segment/creative stay filterable even when not grouped on.
const restrictCategories = (
  categories: AnalyticsCategory[],
  groupBy: string[],
): AnalyticsCategory[] => {
  const selected = new Set(groupBy);
  const restricted: AnalyticsCategory[] = [];
  for (const category of categories) {
    if (category.id === "campaign") {
      restricted.push(category);
      continue;
    }
    const fields = category.fields.filter((field) => selected.has(field));
    if (fields.length === 0) continue;
    const fieldDescriptors: Record<string, AnalyticsFieldDescriptor> = {};
    for (const field of fields) {
      const descriptor = category.fieldDescriptors[field];
      if (descriptor) fieldDescriptors[field] = descriptor;
    }
    restricted.push({ ...category, fields, fieldDescriptors });
  }
  return restricted;
};

export const AnalyticsAggregateFiltersDrawer = ({
  isOpen,
  config,
  value,
  onChange,
  onClose,
  onApply,
  onReset,
}: AnalyticsAggregateFiltersDrawerProps): JSX.Element | null => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  const categories = useMemo(() => {
    const all = buildAnalyticsCategories({
      source: config.source,
      includeCampaign: config.source === "responses",
    });
    return restrictCategories(all, config.groupBy);
  }, [config.source, config.groupBy]);

  const buildConditionsConfig = useMemo(
    () => ({
      categories,
      includeOnly: true,
      lockCombinator: "and" as const,
    }),
    [categories],
  );

  const handleSetGroup = (_group: string, nextConditions: unknown): void => {
    // Include-only + AND-locked => BuildConditions always writes the "all" group.
    onChange({ ...value, all: nextConditions as SegmentDefinition["all"] });
  };

  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setShouldRender(true);
      return undefined;
    }

    setIsVisible(false);

    if (!shouldRender) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
    }, DRAWER_TRANSITION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender || !isOpen) {
      return undefined;
    }

    let firstFrameId = 0;
    let secondFrameId = 0;

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrameId);
      window.cancelAnimationFrame(secondFrameId);
    };
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, shouldRender]);

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  const activeCount = (value.all ?? []).length;

  return createPortal(
    <div
      className={clsx("fixed inset-0 z-[1400]", {
        "pointer-events-auto": shouldRender,
        "pointer-events-none": !shouldRender,
      })}
    >
      <button
        type="button"
        aria-label="Close filters drawer"
        onClick={onClose}
        className={clsx(
          "absolute inset-0 border-0 bg-[rgba(2,6,23,0.62)] p-0 transition-opacity duration-[220ms] ease-out",
          isVisible ? "opacity-100" : "opacity-0",
        )}
      />

      <aside
        aria-modal="true"
        role="dialog"
        className={clsx(
          "absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col overflow-hidden bg-(--surface-1) will-change-transform transition-transform duration-[220ms] ease-out",
          isVisible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            <h2 className="text-[1.05rem] font-semibold text-(--text-primary)">
              Filters
            </h2>
            <p className="mt-0.5 text-[0.78rem] text-(--text-muted)">
              {activeCount} active
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="inline-flex h-8 w-8 items-center justify-center rounded-(--radius-md) border-0 bg-transparent text-(--text-muted) transition hover:bg-(--surface-2) hover:text-(--text-primary)"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {categories.length === 0 ? (
            <div className="rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-4 py-10 text-center">
              <p className="text-sm text-(--text-secondary)">
                Add a property in Configure to filter on it here.
              </p>
            </div>
          ) : (
            <BuildConditions
              definition={value}
              onSetGroup={handleSetGroup}
              config={buildConditionsConfig}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-(--border-subtle) px-5 py-4">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-transparent px-3 text-[13px] font-semibold text-(--text-muted) transition hover:text-(--text-primary)"
          >
            Reset
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-transparent px-3 text-[13px] font-semibold text-(--text-muted) transition hover:text-(--text-primary)"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="inline-flex h-10 items-center justify-center rounded-(--radius-md) border-0 bg-(--brand-primary) px-4 text-[13px] font-bold text-(--brand-primary-contrast) transition hover:bg-(--brand-primary-hover)"
            >
              Apply
            </button>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
